import type { CSSProperties, ReactNode } from 'react';
import { UMIcon, UMInput } from '../../urbanmap/_components';
import { PLAN, UMR } from './tokens';

export function PlanLogo({ size = 40 }: { size?: number }) {
  return (
    <div
      aria-label="Plan"
      style={{
        width: size, height: size, background: PLAN.accent, color: UMR.white, fontWeight: 700,
        fontSize: size > 36 ? 13 : 11, display: 'flex', alignItems: 'center', justifyContent: 'center', letterSpacing: 0.3, flexShrink: 0,
      }}
    >
      Plan
    </div>
  );
}

export function Pill({ children, tone = 'accent', icon, title, onClick }: {
  children: ReactNode; tone?: 'accent' | 'warning' | 'neutral' | 'success' | 'scenario'; icon?: string; title?: string; onClick?: () => void;
}) {
  const palette = {
    accent: { bg: PLAN.accentBg, fg: PLAN.accentStrong, bd: PLAN.accentBorder },
    warning: { bg: PLAN.warningBg, fg: PLAN.warningText, bd: PLAN.warningBorder },
    neutral: { bg: UMR.grey100, fg: UMR.textSecondary, bd: UMR.border },
    success: { bg: PLAN.successBg, fg: PLAN.successText, bd: 'transparent' },
    scenario: { bg: 'var(--nj-core-color-purple-100, #f1eafb)', fg: 'var(--nj-core-color-purple-800, #4d2391)', bd: 'transparent' },
  }[tone];
  const style: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 12, fontSize: 12, lineHeight: '18px',
    background: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`, fontFamily: UMR.font, whiteSpace: 'nowrap',
    cursor: onClick ? 'pointer' : 'default',
  };
  const content = (
    <>
      {icon && <UMIcon name={icon} size={14} color={palette.fg} />}
      {children}
    </>
  );
  return onClick ? <button type="button" title={title} onClick={onClick} style={style}>{content}</button> : <span title={title} style={style}>{content}</span>;
}

export function NumberField({ label, value, unit, onChange, min, hint }: {
  label: string; value: number; unit?: string; onChange: (v: number) => void; min?: number; hint?: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, lineHeight: '21px' }}>
      <span>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <UMInput type="number" value={String(value)} min={min} ariaLabel={label} onChange={(v) => onChange(Number(v) || 0)} />
        </div>
        {unit && <span style={{ fontSize: 12, color: UMR.textSecondary, minWidth: 64 }}>{unit}</span>}
      </div>
      {hint && <span style={{ fontSize: 12, color: UMR.textSecondary }}>{hint}</span>}
    </label>
  );
}

export function Modal({ title, onClose, children, footer, width = 760 }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: number;
}) {
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{ width, maxWidth: '100%', maxHeight: '100%', background: UMR.white, display: 'flex', flexDirection: 'column', fontFamily: UMR.font, color: UMR.black, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}
      >
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: `1px solid ${UMR.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <PlanLogo size={32} />
            <h2 style={{ margin: 0, fontSize: 20, lineHeight: '28px', fontWeight: 700 }}>{title}</h2>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
            <UMIcon name="close" family="icons" size={24} color={UMR.textSecondary} />
          </button>
        </header>
        <div style={{ padding: 24, overflowY: 'auto' }}>{children}</div>
        {footer && <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 24px', borderTop: `1px solid ${UMR.border}` }}>{footer}</footer>}
      </div>
    </div>
  );
}

export function Toast({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      style={{
        position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 120,
        background: UMR.textStrong, color: UMR.white, padding: '10px 16px', fontSize: 14, fontFamily: UMR.font, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
        display: 'flex', alignItems: 'center', gap: 8, maxWidth: 560,
      }}
    >
      <UMIcon name="info" size={18} color={UMR.white} />
      {children}
    </div>
  );
}

export const fmt = (n: number, digits = 0) => n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits });
